import React, { useState } from "react";
import CommentComposer from "../../comments/explorer/CommentComposer";
import styles from "../CanonicalCommentWorkspace.module.css";

type WorkspaceComposerProps = {
  canComment: boolean;
  canCreateAnchoredComment?: boolean;
  hasComments?: boolean;
  targetLabel?: string;
  onCreateRootComment?: (content: string) => Promise<void>;
};

export const WorkspaceComposer: React.FC<WorkspaceComposerProps> = ({
  canComment,
  canCreateAnchoredComment = false,
  hasComments = false,
  targetLabel = "this Gist",
  onCreateRootComment,
}) => {
  const [isOpen, setIsOpen] = useState(!hasComments);

  if (!canComment) {
    return (
      <section className={styles.workspaceAccessCard} aria-label="Comment access">
        <div>
          <strong>Read-only for now</strong>
          <span>Log in to start a new discussion, reply to branches, or create anchored comments.</span>
        </div>
        <div className={styles.workspaceComposerHints}>
          <span className={styles.workspaceComposerHint}>You can still read every thread.</span>
          <span className={styles.workspaceComposerHint}>Anchored markers remain clickable.</span>
        </div>
      </section>
    );
  }

  if (!onCreateRootComment) {
    return (
      <section className={styles.workspaceAccessCard} aria-label="Comment access">
        <div>
          <strong>Discussion is unavailable</strong>
          <span>The workspace can display existing comments, but posting is not connected here yet.</span>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.workspaceComposerCard} aria-label="Start a discussion">
      <div className={styles.workspaceComposerSummary}>
        <div className={styles.workspaceComposerCopy}>
          <strong>{hasComments ? "Add to the discussion" : "Start the first discussion"}</strong>
          <span>
            Start a general thread for {targetLabel}, or select text in the Gist body to create an anchored discussion.
          </span>
        </div>
        <div className={styles.workspaceComposerActions}>
          <button
            type="button"
            className={styles.workspaceComposerToggle}
            onClick={() => setIsOpen((current) => !current)}
          >
            {isOpen ? "Hide composer" : "New general comment"}
          </button>
        </div>
      </div>

      <div className={styles.workspaceComposerHints}>
        <span className={styles.workspaceComposerHint}>General comment = whole Gist.</span>
        <span className={styles.workspaceComposerHint}>
          {canCreateAnchoredComment ? "Anchored comment = selected text." : "Anchored comments depend on text selection."}
        </span>
      </div>

      {isOpen && (
        <div className={styles.workspaceComposerInline}>
          <CommentComposer
            label="Start a new discussion"
            placeholder="Write a general comment about this Gist…"
            submitLabel="Post comment"
            onSubmit={onCreateRootComment}
          />
        </div>
      )}
    </section>
  );
};
